import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import {
  listenerCities,
  listenerCountries,
  listenerMapStats,
} from '@/mocks/listenerMap';
import { mapEntities, mapEntityTypeConfig } from '@/mocks/rfwMap';
import type { MapEntity } from '@/mocks/rfwMap';

type SortMode = 'listeners' | 'activity';

function latLngToPercent(lat: number, lng: number): { top: string; left: string } {
  const latMin = -40;
  const latMax = 42;
  const lngMin = -120;
  const lngMax = -25;
  const top = ((latMax - lat) / (latMax - latMin)) * 100;
  const left = ((lng - lngMin) / (lngMax - lngMin)) * 100;
  return {
    top: `${Math.max(2, Math.min(96, top))}%`,
    left: `${Math.max(2, Math.min(96, left))}%`,
  };
}

const mapRadios: (MapEntity & { pos: { top: string; left: string } })[] = mapEntities
  .filter((e) => e.type === 'radio')
  .map((r) => ({ ...r, pos: latLngToPercent(r.lat, r.lng) }));

function formatNumber(n: number) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

function formatCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

function getEngagementColor(active: number, total: number) {
  const ratio = total > 0 ? active / total : 0;
  if (ratio >= 0.6) return 'bg-emerald-500';
  if (ratio >= 0.4) return 'bg-amber-500';
  return 'bg-rose-400';
}

function getCountryFlag(code: string) {
  const flags: Record<string, string> = {
    CO: '🇨🇴', MX: '🇲🇽', AR: '🇦🇷', ES: '🇪🇸', US: '🇺🇸',
    CL: '🇨🇱', PE: '🇵🇪', EC: '🇪🇨', VE: '🇻🇪', DO: '🇩🇴',
    BR: '🇧🇷', CA: '🇨🇦', GB: '🇬🇧', DE: '🇩🇪', JP: '🇯🇵', AU: '🇦🇺',
  };
  return flags[code] || '🌎';
}

export default function WorldMapPage() {
  const { openModal: openDownloadModal } = useAppDownload();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('listeners');
  const [expandedCountry, setExpandedCountry] = useState<string | null>(null);
  const [hoveredRadio, setHoveredRadio] = useState<number | null>(null);

  const stats = listenerMapStats;

  const sortedCountries = useMemo(() => {
    const list = [...listenerCountries];
    if (sortMode === 'activity') {
      list.sort((a, b) => b.activeListeners - a.activeListeners);
    } else {
      list.sort((a, b) => b.totalListeners - a.totalListeners);
    }
    return list;
  }, [sortMode]);

  const filteredCities = useMemo(() => {
    let cities = [...listenerCities];
    if (selectedCountry) {
      cities = cities.filter((c) => c.country === selectedCountry);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      cities = cities.filter(
        (c) =>
          c.city.toLowerCase().includes(q) ||
          c.country.toLowerCase().includes(q) ||
          c.state.toLowerCase().includes(q)
      );
    }
    return cities;
  }, [selectedCountry, searchTerm]);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-16 md:pt-18">
        {/* ── HERO WITH MAP ── */}
        <section className="relative w-full h-[380px] md:h-[520px] overflow-hidden">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d0!2d-75!3d5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1ses!2sar!4v1"
            className="absolute inset-0 w-full h-full"
            style={{ border: 0, filter: 'saturate(0.6) brightness(0.4)' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Mapa Mundial RadioFrecuenciaWEB"
          ></iframe>

          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/15 to-black/55"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <div className="w-16 h-16 md:w-18 md:h-18 rounded-2xl bg-background-50/10 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/15">
              <i className="ri-global-line text-3xl text-background-50"></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-extrabold text-background-50 leading-tight mb-3">
              Mapa Mundial RFW
            </h1>
            <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed mb-5">
              Visualizá el alcance global de RadioFrecuenciaWEB. Oyentes conectados desde
              múltiples países y ciudades, construyendo comunidad sin fronteras.
            </p>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <span className="px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                <i className="ri-earth-line mr-1"></i>
                {stats.totalCountries} países
              </span>
              <span className="px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                <i className="ri-building-line mr-1"></i>
                {stats.totalCities} ciudades
              </span>
              <span className="px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                <i className="ri-headphone-line mr-1"></i>
                {formatNumber(stats.totalActiveListeners)} activos
              </span>
            </div>
          </div>
        </section>

        {/* ── STATS BAR ── */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 -mt-6 relative z-10">
          <div className="bg-background-50 rounded-2xl p-5 md:p-6 grid grid-cols-2 md:grid-cols-5 gap-4 shadow-sm">
            {[
              { label: 'Países', value: stats.totalCountries, icon: 'ri-global-line', color: 'bg-primary-500' },
              { label: 'Estados/Provincias', value: stats.totalStates, icon: 'ri-map-pin-line', color: 'bg-accent-500' },
              { label: 'Ciudades', value: stats.totalCities, icon: 'ri-building-line', color: 'bg-emerald-500' },
              { label: 'Oyentes Activos', value: formatNumber(stats.totalActiveListeners), icon: 'ri-headphone-line', color: 'bg-amber-500' },
              { label: 'Engagement Promedio', value: `${stats.avgEngagementPct}%`, icon: 'ri-heart-pulse-line', color: 'bg-rose-500' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className={`w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center ${stat.color}`}>
                  <i className={`${stat.icon} text-background-50 text-sm`}></i>
                </div>
                <div className="text-lg md:text-xl font-extrabold text-foreground-950">{stat.value}</div>
                <div className="text-[10px] text-foreground-500 font-medium mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── MAPA CON RADIOS ── */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-8">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                <i className="ri-radio-line text-base text-foreground-600"></i>
              </div>
              <div>
                <h2 className="text-lg font-extrabold text-foreground-950">Radios en el Mapa</h2>
                <p className="text-xs text-foreground-500">{mapRadios.length} radios con ubicación — clickeá para escuchar</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 text-[10px] text-foreground-400">
                <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
                En vivo
              </span>
              <span className="flex items-center gap-1 text-[10px] text-foreground-400">
                <span className="w-2 h-2 rounded-full bg-foreground-400"></span>
                Offline
              </span>
            </div>
          </div>

          <div className="relative w-full h-[420px] md:h-[520px] rounded-2xl overflow-hidden mb-6 bg-background-100">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d0!2d-75!3d5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e1!3m2!1ses!2sar!4v2"
              className="absolute inset-0 w-full h-full"
              style={{ border: 0, filter: 'saturate(0.85) brightness(0.92)' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mapa de Radios RFW"
            ></iframe>

            {/* Radio markers overlay */}
            {mapRadios.map((radio) => {
              const cfg = mapEntityTypeConfig.radio;
              const isHovered = hoveredRadio === radio.id;
              return (
                <div
                  key={radio.id}
                  className="absolute z-20"
                  style={{ top: radio.pos.top, left: radio.pos.left }}
                  onMouseEnter={() => setHoveredRadio(radio.id)}
                  onMouseLeave={() => setHoveredRadio(null)}
                >
                  {/* Marker dot */}
                  <button
                    onClick={() => navigate(radio.linkUrl)}
                    className={`relative w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
                      radio.isLive ? 'bg-rose-500 animate-pulse' : 'bg-foreground-600'
                    } shadow-lg hover:scale-125`}
                    title={radio.name}
                  >
                    <i className="ri-radio-line text-background-50 text-xs md:text-sm"></i>
                  </button>

                  {/* Tooltip */}
                  {isHovered && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-background-50 rounded-xl shadow-lg border border-background-200 p-3 z-30 pointer-events-none">
                      <div className="flex items-center gap-2 mb-2">
                        {radio.isLive && (
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse shrink-0"></span>
                        )}
                        <span className="text-xs font-bold text-foreground-950 leading-tight">{radio.name}</span>
                      </div>
                      <div className="text-[10px] text-foreground-500 space-y-0.5">
                        <div className="flex items-center gap-1">
                          <i className="ri-map-pin-line text-foreground-400 text-[9px]"></i>
                          {radio.city}, {radio.country}
                        </div>
                        {radio.frequency && (
                          <div className="flex items-center gap-1">
                            <i className="ri-broadcast-line text-foreground-400 text-[9px]"></i>
                            {radio.frequency} RFW
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <i className="ri-headphone-line text-foreground-400 text-[9px]"></i>
                          {formatCount(radio.activeListeners)} oyentes activos
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {radio.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="px-1.5 py-0.5 bg-background-100 rounded text-[9px] text-foreground-500">{tag}</span>
                          ))}
                          {radio.isLive && (
                            <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded text-[9px] font-semibold">EN VIVO</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Legend overlay */}
            <div className="absolute bottom-4 left-4 bg-background-50/90 backdrop-blur-sm rounded-xl px-3 py-2 text-[10px] text-foreground-500 z-10">
              <span className="font-semibold text-foreground-700">{mapRadios.length} radios</span> en {(() => { const cs = new Set(mapRadios.map((r) => r.countryCode)); return cs.size; })()} países
            </div>
          </div>

          {/* Radio cards grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {mapRadios.map((radio) => {
              const cfg = mapEntityTypeConfig.radio;
              return (
                <button
                  key={radio.id}
                  onClick={() => navigate(radio.linkUrl)}
                  className="bg-background-50 rounded-2xl p-4 text-left cursor-pointer transition-all hover:shadow-sm group border border-transparent hover:border-background-200"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {radio.isLive ? (
                      <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse shrink-0"></span>
                    ) : (
                      <span className="w-2.5 h-2.5 rounded-full bg-foreground-400 shrink-0"></span>
                    )}
                    <span className="text-xs font-bold text-foreground-950 truncate group-hover:text-primary-600 transition-colors">
                      {radio.name}
                    </span>
                  </div>
                  <div className="text-[10px] text-foreground-500 space-y-0.5">
                    {radio.frequency && (
                      <div className="font-semibold text-foreground-700">{radio.frequency}</div>
                    )}
                    <div className="flex items-center gap-1">
                      <span>{getCountryFlag(radio.countryCode)}</span>
                      <span>{radio.city}</span>
                    </div>
                    <div>{formatCount(radio.activeListeners)} oyentes</div>
                    {radio.isLive && (
                      <span className="inline-block mt-1 px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded text-[9px] font-semibold">
                        EN VIVO
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* ── ALCANCE POR PAÍS ── */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-10">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                <i className="ri-flag-line text-base text-foreground-600"></i>
              </div>
              <div>
                <h2 className="text-lg font-extrabold text-foreground-950">Alcance por País</h2>
                <p className="text-xs text-foreground-500">{sortedCountries.length} países con presencia RFW</p>
              </div>
            </div>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {([
                { key: 'listeners' as SortMode, label: 'Oyentes Totales' },
                { key: 'activity' as SortMode, label: 'Actividad' },
              ]).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortMode(opt.key)}
                  className={`px-3.5 py-1.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    sortMode === opt.key
                      ? 'bg-background-50 text-foreground-950 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {sortedCountries.map((country) => {
              const isExpanded = expandedCountry === country.country;
              const engRatio = country.totalListeners > 0
                ? (country.activeListeners / country.totalListeners) * 100
                : 0;

              return (
                <div key={country.country} className="relative">
                  <div
                    className={`bg-background-50 rounded-2xl p-4 cursor-pointer transition-all group ${
                      selectedCountry === country.country
                        ? 'ring-2 ring-primary-500 shadow-sm'
                        : 'hover:shadow-sm'
                    }`}
                    onClick={() => {
                      setSelectedCountry(selectedCountry === country.country ? null : country.country);
                      setExpandedCountry(expandedCountry === country.country ? null : country.country);
                      setSearchTerm('');
                    }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">{getCountryFlag(country.countryCode)}</span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xs font-bold text-foreground-950 truncate">{country.country}</h3>
                        <span className="text-[10px] text-foreground-500">{country.cityCount} ciudades</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] text-foreground-500">Oyentes</span>
                          <span className="text-[10px] font-bold text-foreground-700">
                            {formatNumber(country.totalListeners)}
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-500 rounded-full"
                            style={{ width: `${Math.min((country.totalListeners / sortedCountries[0].totalListeners) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] text-foreground-500">Activos</span>
                          <span className="text-[10px] font-bold text-foreground-700">
                            {formatNumber(country.activeListeners)}
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getEngagementColor(country.activeListeners, country.totalListeners)}`}
                            style={{ width: `${engRatio}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 mt-3 text-[10px] text-foreground-500">
                      <i className="ri-map-pin-line text-foreground-400"></i>
                      {country.stateCount} estados
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-2xl shadow-lg z-20 p-3 max-h-64 overflow-y-auto border border-background-200">
                      <div className="text-[10px] font-bold text-foreground-500 mb-2 uppercase tracking-wider">
                        Ciudades en {country.country}
                      </div>
                      <div className="space-y-1.5">
                        {listenerCities
                          .filter((c) => c.country === country.country)
                          .map((city) => (
                            <div key={city.id} className="flex items-center justify-between text-xs py-1">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className="w-1.5 h-1.5 rounded-full bg-accent-500 shrink-0"></span>
                                <span className="text-foreground-800 truncate">{city.city}</span>
                              </div>
                              <span className="text-foreground-500 shrink-0 ml-2">
                                {formatNumber(city.activeListeners)} / {formatNumber(city.totalListeners)}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* ── CITY SEARCH ── */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-10">
          <div className="bg-background-50 rounded-2xl p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                  <i className="ri-search-line text-base text-foreground-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-foreground-950">Todas las Ciudades</h3>
                  <p className="text-xs text-foreground-500">
                    {filteredCities.length} de {listenerCities.length} ciudades
                    {selectedCountry && ` en ${selectedCountry}`}
                  </p>
                </div>
              </div>

              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Buscar ciudad..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2.5 bg-background-100 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-400 w-full sm:w-56 border border-transparent focus:border-primary-500 focus:outline-none transition-all"
                />
              </div>
            </div>

            {selectedCountry && (
              <button
                onClick={() => {
                  setSelectedCountry(null);
                  setExpandedCountry(null);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-full text-[11px] font-semibold mb-4 cursor-pointer hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                <i className="ri-close-line"></i>
                {selectedCountry}
              </button>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[420px] overflow-y-auto">
              {filteredCities.map((city) => (
                <div
                  key={city.id}
                  className="bg-background-100 rounded-xl p-3 hover:bg-background-200/70 transition-colors"
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-sm">{getCountryFlag(city.countryCode)}</span>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-bold text-foreground-950 truncate">{city.city}</h4>
                      <span className="text-[10px] text-foreground-500">{city.state}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-foreground-500 flex items-center gap-1">
                      <i className="ri-headphone-line text-foreground-400"></i>
                      {formatNumber(city.activeListeners)} activos
                    </span>
                    <span className="text-foreground-400">{formatNumber(city.totalListeners)} total</span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-background-200/70">
                    <div className="text-[9px] text-foreground-400 truncate">
                      <i className="ri-radio-line mr-0.5 text-foreground-300"></i>
                      {city.topRadio}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredCities.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-background-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-foreground-400 text-xl"></i>
                </div>
                <p className="text-sm text-foreground-500">No se encontraron ciudades</p>
                <p className="text-xs text-foreground-400 mt-1">Probá cambiando los filtros o el término de búsqueda</p>
              </div>
            )}
          </div>
        </section>

        {/* ── INSIGHT CARDS ── */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-background-50 rounded-2xl p-5">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
                <i className="ri-earth-line text-emerald-600"></i>
              </div>
              <h4 className="text-sm font-bold text-foreground-950 mb-1">Latinoamérica Lidera</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">
                Colombia, México y Argentina concentran el 48% de la audiencia global activa de RFW.
              </p>
            </div>
            <div className="bg-background-50 rounded-2xl p-5">
              <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center mb-3">
                <i className="ri-flight-takeoff-line text-sky-600"></i>
              </div>
              <h4 className="text-sm font-bold text-foreground-950 mb-1">Expansión Intercontinental</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">
                Presencia en 4 continentes con oyentes en Europa, Asia, Oceanía y toda América.
              </p>
            </div>
            <div className="bg-background-50 rounded-2xl p-5">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                <i className="ri-smartphone-line text-amber-600"></i>
              </div>
              <h4 className="text-sm font-bold text-foreground-950 mb-1">Mobile First</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">
                El 78% de los oyentes se conectan desde dispositivos móviles en cualquier lugar.
              </p>
            </div>
            <div className="bg-background-50 rounded-2xl p-5">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center mb-3">
                <i className="ri-pulse-line text-rose-600"></i>
              </div>
              <h4 className="text-sm font-bold text-foreground-950 mb-1">Crecimiento Constante</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">
                +36% de crecimiento interanual en nuevas ciudades conectadas a la red RFW.
              </p>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-10 mb-12">
          <div className="bg-background-50 rounded-2xl p-8 md:p-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
              <i className="ri-earth-line text-background-50 text-3xl"></i>
            </div>
            <h2 className="text-xl font-extrabold text-foreground-950 mb-2">
              ¿Querés que tu radio aparezca en el mapa?
            </h2>
            <p className="text-sm text-foreground-500 mb-5 max-w-lg mx-auto">
              Registrá tu radio en RFW y conectate con oyentes de todo el mundo. Sin fronteras, sin límites.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/register-radio"
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-circle-line mr-1.5"></i>
                Registrar Mi Radio
              </Link>
              <button
                onClick={openDownloadModal}
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line mr-1.5"></i>
                Descargar App RFW
              </button>
              <Link
                to="/descubrir"
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-compass-3-line mr-1.5"></i>
                Descubrir Comunidades
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <div className="md:hidden h-20"></div>
    </div>
  );
}